import { useCallback, useEffect, useRef, useState } from 'react';
import { getActiveJob } from '../services/projects';
import { listVersions } from '../services/versions';
import type { JobStatusMessage } from '../models/pipelineEvents';
import { usePipelineEvents } from '../context/PipelineEventsContext';

/** What the running job is doing for the creator: making a value editable, or any other change or build. */
export type VersionJobActivity = 'exposing' | 'versioning';

export type VersionJobState =
  | { phase: 'idle' }
  | { phase: 'running'; activity: VersionJobActivity }
  | { phase: 'ready'; semver: string }
  | { phase: 'failed'; message: string | null };

export interface VersionJob {
  state: VersionJobState;
  running: boolean;
  /** The server accepted a request that makes a version, as job `jobId`. */
  started: (jobId: string, activity: VersionJobActivity) => void;
  dismiss: () => void;
  /** Reads the project's active job again, for an outcome whose event may have been missed. */
  recheck: () => void;
}

const SettleMs = 2000;
const Idle: VersionJobState = { phase: 'idle' };

/**
 * The project's job that produces a version, a chat request or a build, as the creator follows it from the play
 * page. It runs while the job is queued or processed; when it ends, a newer active version than the one playing
 * is ready, or the failure is shown, until dismissed. Needs the page's `PipelineEventsProvider` for `projectId`.
 */
export function useVersionJob(projectId: string, playingVersionId: string): VersionJob {
  const [state, setState] = useState<VersionJobState>(Idle);
  const exposeJobIds = useRef(new Set<string>());
  const settleTimer = useRef<number | undefined>(undefined);

  const activityOf = useCallback(
    (jobId: string): VersionJobActivity => (exposeJobIds.current.has(jobId.toLowerCase()) ? 'exposing' : 'versioning'),
    [],
  );

  const start = useCallback(
    (activity: VersionJobActivity) =>
      setState((previous) => (previous.phase === 'running' ? previous : { phase: 'running', activity })),
    [],
  );

  const projectIdRef = useRef(projectId);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  const finish = useCallback(async () => {
    const active = await listVersions(projectId).then(
      (versions) => versions.find((version) => version.isActive),
      () => undefined,
    );
    if (projectIdRef.current !== projectId) return;
    const newer = active && active.id.toLowerCase() !== playingVersionId.toLowerCase() && active.webBuildState === 'Ready';
    setState(newer ? { phase: 'ready', semver: active.semver } : Idle);
  }, [projectId, playingVersionId]);

  const runningRef = useRef(false);
  useEffect(() => {
    runningRef.current = state.phase === 'running';
  }, [state]);

  const read = useCallback(() => {
    getActiveJob(projectId)
      .then((job) => {
        if (projectIdRef.current !== projectId) return;
        if (!job) {
          if (runningRef.current) void finish();
        } else if (job.jobType !== 'Organize') {
          start(activityOf(job.jobId));
        }
      })
      .catch(() => undefined);
  }, [projectId, start, activityOf, finish]);

  // The worker publishes a job's last message before it marks the job finished, so the state is read back
  // only after that gap.
  const readAfterSettling = useCallback(() => {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(read, SettleMs);
  }, [read]);

  useEffect(() => {
    read();
    return () => window.clearTimeout(settleTimer.current);
  }, [read]);

  const onJobStatus = useCallback(
    (message: JobStatusMessage) => {
      if (message.jobType === 'Organize') return;
      if (message.status === 'Processing') setState({ phase: 'running', activity: activityOf(message.jobId) });
      if (message.status === 'Completed') void finish();
      if (message.status === 'Failed') setState({ phase: 'failed', message: message.failureMessage });
      if (message.status === 'Cancelled') setState(Idle);
    },
    [activityOf, finish],
  );

  usePipelineEvents({
    onReconnected: read,
    onJobStatus,
    onMilestone: (message) => start(activityOf(message.jobId)),
    onAssistantMessageReady: readAfterSettling,
    onError: readAfterSettling,
    onBuildStateChanged: (message) => (message.state === 'Building' ? start('versioning') : readAfterSettling()),
  });

  const started = useCallback((jobId: string, activity: VersionJobActivity) => {
    if (activity === 'exposing') exposeJobIds.current.add(jobId.toLowerCase());
    setState({ phase: 'running', activity });
  }, []);

  const dismiss = useCallback(() => setState(Idle), []);

  return { state, running: state.phase === 'running', started, dismiss, recheck: read };
}
